import React, { useState } from 'react';
import { Card, CardHeader, CardContent, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/hooks/use-toast';
import { Plus, Pencil, Trash2, Ruler } from 'lucide-react';
import {
  useGetMeasurementUnits,
  useCreateMeasurementUnit,
  useUpdateMeasurementUnit,
  useDeleteMeasurementUnit,
  MeasurementUnit,
} from '@/hooks/use-measurement-units';

export default function UnitManagement() {
  const { data: units, isLoading } = useGetMeasurementUnits();
  const createUnit = useCreateMeasurementUnit();
  const updateUnit = useUpdateMeasurementUnit();
  const deleteUnit = useDeleteMeasurementUnit();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<MeasurementUnit | null>(null);
  const [name, setName] = useState('');
  const [abbreviation, setAbbreviation] = useState('');
  const [status, setStatus] = useState('active');

  const openAdd = () => {
    setEditing(null);
    setName('');
    setAbbreviation('');
    setStatus('active');
    setDialogOpen(true);
  };

  const openEdit = (unit: MeasurementUnit) => {
    setEditing(unit);
    setName(unit.name);
    setAbbreviation(unit.abbreviation);
    setStatus(unit.status);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!name.trim() || !abbreviation.trim()) {
      toast({ title: 'Validation', description: 'Name and abbreviation are required.', variant: 'destructive' });
      return;
    }
    try {
      if (editing) {
        await updateUnit.mutateAsync({ id: editing.id, name: name.trim(), abbreviation: abbreviation.trim(), status });
        toast({ title: 'Updated', description: `"${name}" updated successfully.` });
      } else {
        await createUnit.mutateAsync({ name: name.trim(), abbreviation: abbreviation.trim() });
        toast({ title: 'Created', description: `"${name}" added successfully.` });
      }
      setDialogOpen(false);
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to save unit.', variant: 'destructive' });
    }
  };

  const handleDelete = async (unit: MeasurementUnit) => {
    if (!confirm(`Delete "${unit.name} (${unit.abbreviation})"?`)) return;
    try {
      await deleteUnit.mutateAsync(unit.id);
      toast({ title: 'Deleted', description: `"${unit.name}" removed.` });
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Failed to delete.', variant: 'destructive' });
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Ruler className="h-5 w-5" />
          Measurement Units
        </CardTitle>
        <Button size="sm" onClick={openAdd} className="gap-1">
          <Plus className="h-4 w-4" />
          Add Unit
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-muted-foreground text-sm">Loading...</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Abbreviation</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {units?.map((unit) => (
                <TableRow key={unit.id}>
                  <TableCell className="font-medium">{unit.name}</TableCell>
                  <TableCell>{unit.abbreviation}</TableCell>
                  <TableCell>
                    <Badge variant={unit.status === 'active' ? 'default' : 'secondary'}>
                      {unit.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(unit)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(unit)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {(!units || units.length === 0) && (
                <TableRow>
                  <TableCell colSpan={4} className="text-center text-muted-foreground">
                    No measurement units found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Unit' : 'Add Unit'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input placeholder="e.g. Kilogram" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Abbreviation</Label>
              <Input placeholder="e.g. kg" value={abbreviation} onChange={(e) => setAbbreviation(e.target.value)} />
            </div>
            {editing && (
              <div className="flex items-center gap-3">
                <Label>Active</Label>
                <Switch checked={status === 'active'} onCheckedChange={(v) => setStatus(v ? 'active' : 'inactive')} />
              </div>
            )}
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleSave} disabled={createUnit.isPending || updateUnit.isPending}>
              {editing ? 'Update' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
